import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../App';
import { User, Menu, X, Globe } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DarkModeToggle from './DarkModeToggle';
import { useTheme } from './ThemeProvider';
import HjezlyLogo from './HjezlyLogo';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, setShowLoginModal } = useAppContext();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isHomepage = location.pathname === '/';
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    signOut(auth).then(() => {
      setMenuOpen(false);
      navigate('/');
    }).catch((error) => {
      console.error('Logout Error:', error);
    });
  };

  const toggleLanguage = () => {
    // 3. This now tells i18next to change the language
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  const isCustomer = user && user.phoneNumber;

  return (
    <header className="bg-white dark:bg-neutral-900 shadow-md sticky top-0 z-40 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          
          {/* Left Side: Logo or Back Button */}
          <div className="flex-shrink-0 flex items-center">
            {!isHomepage && (
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-neutral-600 dark:text-neutral-300 hover:text-primary dark:hover:text-primary transition-colors mr-4"
              >
                <ChevronLeft size={24} />
              </button>
            )}
            
            <Link to="/" className="flex items-center gap-2 group">
              <HjezlyLogo size="sm" />
            </Link>
          </div>

          {/* Center: Navigation (Desktop) */}
          <nav className="hidden md:flex space-x-8">
            <Link to="/stadiums" className="text-neutral-600 dark:text-neutral-300 hover:text-primary dark:hover:text-primary px-3 py-2 rounded-md text-sm font-medium transition-colors">
              {t('header.stadiums')}
            </Link>
            <Link to="/tournaments" className="text-neutral-600 dark:text-neutral-300 hover:text-primary dark:hover:text-primary px-3 py-2 rounded-md text-sm font-medium transition-colors">
              {t('header.tournaments')}
            </Link>
            <Link to="/about" className="text-neutral-600 dark:text-neutral-300 hover:text-primary dark:hover:text-primary px-3 py-2 rounded-md text-sm font-medium transition-colors">
              {t('header.about')}
            </Link>
            <Link to="/contact" className="text-neutral-600 dark:text-neutral-300 hover:text-primary dark:hover:text-primary px-3 py-2 rounded-md text-sm font-medium transition-colors">
              {t('header.contact')}
            </Link>
          </nav>

          {/* Right Side: Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Dark Mode Toggle */}
            <DarkModeToggle />
            
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300 hover:text-primary dark:hover:text-primary transition-colors font-medium"
            >
              <Globe size={20} />
              <span>{t('header.language')}</span>
            </button>

            {isCustomer ? (
              // Logged-in Customer Menu
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center gap-2 text-neutral-700 dark:text-neutral-200 font-medium hover:text-primary dark:hover:text-primary transition-colors"
                >
                  <User size={20} />
                  <span>{user.name || user.displayName || user.phoneNumber}</span>
                </button>
                {menuOpen && (
                  <div className="absolute top-10 right-0 bg-white dark:bg-neutral-800 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-700 mt-2 w-48 overflow-hidden animate-fade-in z-[99]">
                    <Link
                      to="/my-bookings"
                      onClick={() => setMenuOpen(false)}
                      className="block w-full text-left px-4 py-3 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                    >
                      {t('header.myBookings')}
                    </Link>
                    <Link
                      to="/favorites"
                      onClick={() => setMenuOpen(false)}
                      className="block w-full text-left px-4 py-3 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                    >
                      {t('header.favorites') || 'Favorites'}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-3 text-red-600 dark:text-red-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                    >
                      {t('header.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // --- NEW GUEST BUTTON ---
              <button
                onClick={() => setShowLoginModal(true)}
                className="bg-primary text-white px-5 py-2 rounded-xl font-semibold hover:bg-primary-dark transition-colors"
              >
                {t('loginmodal.title')}
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={28} className="text-neutral-700 dark:text-neutral-200" /> : <Menu size={28} className="text-neutral-700 dark:text-neutral-200" />}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Menu (Dropdown) */}
      {menuOpen && (
        <div className="md:hidden bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 shadow-lg animate-fade-in">
          <div className="px-4 py-5 space-y-2">
            {isCustomer ? (
              // --- Logged-in Customer Menu ---
              <>
                <Link
                  to="/my-bookings"
                  onClick={() => setMenuOpen(false)}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-neutral-700 dark:text-neutral-200 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  {t('header.myBookings')}
                </Link>
                <Link
                  to="/favorites"
                  onClick={() => setMenuOpen(false)}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-neutral-700 dark:text-neutral-200 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  {t('header.favorites') || 'Favorites'}
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-red-600 dark:text-red-400 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  {t('header.logout')}
                </button>
                <div className="border-t border-neutral-200 dark:border-neutral-700 my-2"></div>
              </>
            ) : (
              // --- Guest Menu ---
              <>
                <button
                  onClick={() => {
                    setShowLoginModal(true);
                    setMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-primary dark:text-primary-400 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  {t('loginmodal.title')}
                </button>
                <div className="border-t border-neutral-200 dark:border-neutral-700 my-2"></div>
              </>
            )}
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 w-full text-left px-3 py-2 text-base font-medium text-neutral-700 dark:text-neutral-200 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              {/* Dark mode icon */}
              <div className="flex-shrink-0">
                {theme === 'dark' ? (
                  // Sun icon for light mode
                  <svg
                    className="w-5 h-5 text-yellow-500"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>
                  </svg>
                ) : (
                  // Moon icon for dark mode
                  <svg
                    className="w-5 h-5 text-neutral-700 dark:text-neutral-300"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path>
                  </svg>
                )}
              </div>
              <span>{t('header.darkMode')}</span>
            </button>
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 w-full text-left px-3 py-2 text-base font-medium text-neutral-700 dark:text-neutral-200 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <Globe size={20} />
              <span>{t('header.language')}</span>
            </button>
          </div>
        </div>
      )}

      {/* We need this simple animation */}
      
    </header>
  );
};

export default Header;